import React, {useCallback, useMemo, useState} from 'react'
import ReusableSnackbar from '../components/Snackbar/ReusableSnackbar'

// Error type definitions for better type safety
interface ApiError {
  response?: {
    data?: {
      customData?: {
        title?: string
      }
      message?: string
    }
  }
  message?: string
}

export interface SnackbarState {
  isOpen: boolean
  message: string
  severity: 'error' | 'warning' | 'info' | 'success'
}

export const useSnackbarWithComponent = () => {
  const [snackbarState, setSnackbarState] = useState<SnackbarState>({
    isOpen: false,
    message: '',
    severity: 'error',
  })

  const showSnackbar = useCallback(
    (message: string, severity: 'error' | 'warning' | 'info' | 'success' = 'error') => {
      setSnackbarState({
        isOpen: true,
        message,
        severity,
      })
    },
    [],
  )

  // Enhanced error handler that intelligently extracts error messages from catch blocks
  const showError = useCallback(
    (error: unknown) => {
      console.error('Error caught:', error)

      // Check for custom error title first (highest priority)
      if (error?.response?.data?.customData?.title?.length) {
        showSnackbar(error.response.data.customData.title, 'error')
      }
      // Check for standard error message
      else if (error?.response?.data?.message?.length) {
        showSnackbar(error.response.data.message, 'error')
      }
      // Check for direct error message
      else if (error?.message?.length) {
        showSnackbar(error.message, 'error')
      } else if (error?.data?.message?.length) {
        showSnackbar(error.data.message, 'error')
      }
      // Fallback to provided message or default
      else {
        showSnackbar(error, 'error')
      }
    },
    [showSnackbar],
  )

  const showSuccess = useCallback(
    (message: string) => showSnackbar(message, 'success'),
    [showSnackbar],
  )
  const showWarning = useCallback(
    (message: string) => showSnackbar(message, 'warning'),
    [showSnackbar],
  )
  const showInfo = useCallback((message: string) => showSnackbar(message, 'info'), [showSnackbar])

  const hideSnackbar = useCallback(() => {
    setSnackbarState((prev) => ({...prev, isOpen: false}))
  }, [])

  // The snackbar component that can be rendered in JSX - memoized to prevent recreation on every render
  const SnackbarComponent = useMemo(
    () => () => (
      <ReusableSnackbar
        isOpen={snackbarState.isOpen}
        setIsSnackbarOpen={hideSnackbar}
        customMessage={snackbarState.message}
        severity={snackbarState.severity}
      />
    ),
    [snackbarState.isOpen, snackbarState.message, snackbarState.severity, hideSnackbar],
  )

  return {
    snackbarState,
    showSnackbar,
    showError,
    showSuccess,
    showWarning,
    showInfo,
    hideSnackbar,
    SnackbarComponent, // Ready-to-use component
  }
}
